import { cn } from "@/lib/utils";

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "avatar" | "card" | "text";
  animate?: "pulse" | "wave";
  width?: string | number;
  height?: string | number;
}

function Skeleton({
  className,
  variant = "default",
  animate = "pulse",
  width,
  height,
  ...props
}: SkeletonProps) {
  const variants = {
    default: "rounded-md",
    avatar: "rounded-full",
    card: "rounded-lg",
    text: "rounded-sm h-4 w-full",
  };

  const animations = {
    pulse: "animate-pulse",
    wave: "animate-shimmer bg-linear-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-800 dark:via-slate-700 dark:to-slate-800 bg-size-[400%_100%]",
  };

  return (
    <div
      className={cn(
        variants[variant],
        animations[animate],
        "bg-gray-100 dark:bg-gray-800",
        className,
      )}
      style={{
        width: width,
        height: height,
      }}
      {...props}
    />
  );
}

interface ItemSkeletonProps {
  /** Number of items to render */
  numberOfItems?: number;
  /** Whether to show nested items under each group */
  showNestedItems?: boolean;
  /** Number of nested items per group */
  nestedItemsCount?: number;
  /** Custom container className */
  containerClassName?: string;
  /** Custom group item className */
  groupItemClassName?: string;
  /** Custom nested item className */
  nestedItemClassName?: string;
}

/**
 * Renders skeleton loading items for groups and nested items
 */
const ItemSkeleton = ({
  numberOfItems = 2,
  showNestedItems = true,
  nestedItemsCount = 2,
  containerClassName,
  groupItemClassName,
  nestedItemClassName,
}: ItemSkeletonProps) => {
  return (
    <div className={cn("space-y-2", containerClassName)}>
      {Array.from({ length: numberOfItems }, (_, i) => (
        <div key={i} className="space-y-1">
          {/* Group header skeleton */}
          <Skeleton className={cn("h-5 px-2", groupItemClassName)} />

          {/* Nested items skeletons */}
          {showNestedItems && (
            <div className="pl-6 space-y-1 pr-2">
              {Array.from({ length: nestedItemsCount }, (_, j) => (
                <Skeleton key={j} className={cn("h-5", nestedItemClassName)} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export { Skeleton, ItemSkeleton };
